// ⏰ The clock next to Send (owner, 2026-10-10): « tomorrow morning, tonight,
// or a specific date — good UI and UX ». One tap for the usual times, a date
// and an hour for the rest; every time is Morocco's (lib/scheduleTime.ts).
// The list of what is waiting, with « send now » and « cancel », sits above
// the box (ScheduledStrip) so a scheduled message is never out of sight.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { whatsappApi } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { casaDateInput, casaInputToMs, quickSlots, whenLabel, type QuickSlot } from '@/lib/scheduleTime';
import { scheduledChip, type ScheduledChip as ScheduledChipT } from '@/lib/sendVisibility';

const v = (name: string) => `var(--wa-${name})`;
const C = {
  card: v('in'), field: v('field'), text: v('text'), meta: v('meta'), green: v('green'), error: v('error'), chip: v('chip'), chipInk: v('chip-ink'),
} as const;

type IconName = 'clock' | 'sun' | 'moon' | 'calendar';
export function ScheduleIcon({ name, className = '' }: { name: IconName; className?: string }) {
  const d: Record<IconName, string> = {
    clock: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0-13.5V12l3 2',
    sun: 'M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm0-13v2m0 14v2M4.2 4.2l1.4 1.4m12.8 12.8 1.4 1.4M3 12h2m14 0h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
    moon: 'M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z',
    calendar: 'M7 3v3m10-3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z',
  };
  return (
    <svg aria-hidden viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d={d[name]} />
    </svg>
  );
}

/** whenLabel in the page's language. */
export function useWhen(): (ms: number, nowMs: number) => string {
  const { t, lang } = useT();
  return (ms, nowMs) => whenLabel(ms, nowMs, { today: t('Aujourd’hui'), tomorrow: t('Demain'), locale: lang === 'en' ? 'en-GB' : 'fr-FR' });
}

const SLOT_ICON: Record<QuickSlot['key'], IconName> = {
  this_morning: 'sun', tonight: 'moon', tomorrow_morning: 'sun', tomorrow_evening: 'moon',
};

/** The popover above the clock: quick times, or a date and an hour. */
export function SchedulePicker({ onPick, onClose, busy = false, error = null }: {
  onPick: (ms: number) => void;
  onClose: () => void;
  busy?: boolean;
  error?: string | null;
}) {
  const { t } = useT();
  const when = useWhen();
  const [nowMs] = useState(() => Date.now());
  const slots = useMemo(() => quickSlots(nowMs), [nowMs]);
  const [custom, setCustom] = useState(false);
  const [date, setDate] = useState(() => casaDateInput(nowMs + 86_400_000));
  const [time, setTime] = useState('09:00');
  const ref = useRef<HTMLDivElement | null>(null);

  const customMs = casaInputToMs(date, time);
  const customOk = customMs != null && customMs - Date.now() >= 60_000;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const onDown = (e: MouseEvent) => {
      const el = ref.current;
      if (el && !el.contains(e.target as Node) && !(e.target as HTMLElement).closest?.('[data-schedule-toggle]')) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown); };
  }, [onClose]);

  const row = 'w-full flex items-center gap-3 rounded-lg px-3 min-h-11 text-left hover:bg-[var(--wa-field)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a884] disabled:opacity-50';
  return (
    <div ref={ref} role="dialog" aria-label={t('Programmer l’envoi')}
      className="absolute bottom-full right-0 mb-2 z-40 w-[min(19rem,calc(100vw-1.5rem))] rounded-xl border border-black/10 dark:border-white/10 p-2 shadow-xl"
      style={{ backgroundColor: C.card, color: C.text }}>
      <div className="flex items-baseline justify-between px-3 pt-1 pb-2">
        <p className="text-[14px] font-semibold">{t('Programmer l’envoi')}</p>
        <p className="text-[11px]" style={{ color: C.meta }}>{t('heure du Maroc')}</p>
      </div>

      {!custom && (
        <div className="space-y-0.5">
          {slots.map((s) => (
            <button key={s.key} type="button" className={row} disabled={busy} onClick={() => onPick(s.ms)}>
              <ScheduleIcon name={SLOT_ICON[s.key]} className="h-5 w-5 shrink-0" />
              <span className="flex-1 text-[14px]">{t(s.label)}</span>
              <span className="text-[12.5px] tabular-nums" style={{ color: C.meta }}>{when(s.ms, nowMs)}</span>
            </button>
          ))}
          <div className="my-1 h-px bg-black/10 dark:bg-white/10" />
          <button type="button" className={row} disabled={busy} onClick={() => setCustom(true)}>
            <ScheduleIcon name="calendar" className="h-5 w-5 shrink-0" />
            <span className="flex-1 text-[14px]">{t('Choisir une date et une heure')}</span>
          </button>
        </div>
      )}

      {custom && (
        <form className="px-2 pb-1 space-y-3" onSubmit={(e) => { e.preventDefault(); if (customOk && customMs != null) onPick(customMs); }}>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <label className="text-[11.5px] space-y-1" style={{ color: C.meta }}>
              <span>{t('Date')}</span>
              <input type="date" value={date} min={casaDateInput(Date.now())} onChange={(e) => setDate(e.target.value)} required
                className="block w-full rounded-lg px-2.5 h-10 text-[15px] md:text-[14px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a884]"
                style={{ backgroundColor: C.field, color: C.text }} />
            </label>
            <label className="text-[11.5px] space-y-1" style={{ color: C.meta }}>
              <span>{t('Heure')}</span>
              <input type="time" value={time} step={300} onChange={(e) => setTime(e.target.value)} required
                className="block w-28 rounded-lg px-2.5 h-10 text-[15px] md:text-[14px] tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00a884]"
                style={{ backgroundColor: C.field, color: C.text }} />
            </label>
          </div>
          <p className="text-[12.5px]" style={{ color: customOk ? C.meta : C.error }} aria-live="polite">
            {customMs == null ? t('Choisissez une date et une heure.')
              : customOk ? `${t('Partira')} ${when(customMs, Date.now())}`
                : t('Cette heure est déjà passée.')}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setCustom(false)} className="h-10 px-3 rounded-lg text-[13.5px] hover:bg-[var(--wa-field)]" style={{ color: C.meta }}>
              {t('Retour')}
            </button>
            <span className="flex-1" />
            <button type="submit" disabled={!customOk || busy}
              className="h-10 px-4 rounded-lg text-[14px] font-medium text-white disabled:opacity-40" style={{ backgroundColor: C.green }}>
              {busy ? t('…') : t('Programmer')}
            </button>
          </div>
        </form>
      )}
      {error && <p className="px-3 pt-2 text-[12px]" style={{ color: C.error }} role="alert">{error}</p>}
    </div>
  );
}

/**
 * The chat's scheduled messages and what became of them (owner, 2026-10-15):
 * what still waits, with « send now » and « cancel »; what failed, was held or
 * is late, with WHY — always in sight; and the last 7 days' sent / cancelled
 * ones behind « Historique ». Nothing that did not leave is out of sight.
 */
export function ScheduledStrip({ chatId }: { chatId: string }) {
  const { t } = useT();
  const when = useWhen();
  const qc = useQueryClient();
  const [history, setHistory] = useState(false);
  const q = useQuery({
    queryKey: ['whatsapp-scheduled', chatId],
    queryFn: () => whatsappApi.scheduledReplies(chatId),
    refetchInterval: 60_000,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['whatsapp-scheduled', chatId] });
    void qc.invalidateQueries({ queryKey: ['whatsapp-conversation', chatId] });
  };
  const cancel = useMutation({ mutationFn: (id: string) => whatsappApi.cancelScheduledReply(id), onSettled: refresh });
  const now = useMutation({ mutationFn: (id: string) => whatsappApi.sendScheduledNow(id), onSettled: refresh });
  useEffect(() => { setHistory(false); }, [chatId]);
  const nowMs = Date.now();
  const rows = (q.data?.scheduled ?? []).map((r) => ({ r, chip: scheduledChip(r, nowMs, when, t) }));
  // In sight: everything that has not left, and every bad news. Behind the
  // toggle: what went out, or was cancelled.
  const shown = rows.filter(({ chip }) => chip.actions || chip.tone === 'bad' || chip.tone === 'wait');
  const past = rows.filter((x) => !shown.includes(x));
  if (!rows.length) return null;
  const err = (cancel.error ?? now.error) as Error | null;
  const ink: Record<ScheduledChipT['tone'], string> = { wait: C.chipInk, ok: C.meta, bad: C.error, muted: C.meta };
  const line = ({ r, chip }: (typeof rows)[number]) => (
    <div key={r.id} className="flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg px-3 py-2"
      style={{ backgroundColor: chip.tone === 'wait' ? C.chip : C.card, color: C.text, border: chip.tone === 'bad' ? `1px solid ${C.error}` : undefined }}>
      <ScheduleIcon name="clock" className="h-4 w-4 shrink-0" />
      <div className="min-w-[11rem] flex-1">
        <p className="text-[12.5px] font-medium tabular-nums" style={{ color: ink[chip.tone] }}>{chip.label}</p>
        <p className="text-[12.5px] truncate opacity-80">{r.body}</p>
        {chip.why && <p className="text-[12px] break-words" style={{ color: chip.tone === 'bad' ? C.error : C.meta }}>{chip.why}</p>}
      </div>
      {chip.actions && (
        <div className="ml-auto flex items-center">
          <button type="button" onClick={() => now.mutate(r.id)} disabled={now.isPending || cancel.isPending}
            className="shrink-0 h-8 px-2.5 rounded-md text-[12.5px] font-medium hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40">
            {t('Envoyer maintenant')}
          </button>
          <button type="button" onClick={() => cancel.mutate(r.id)} disabled={now.isPending || cancel.isPending}
            className="shrink-0 h-8 px-2.5 rounded-md text-[12.5px] hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40"
            aria-label={t('Annuler ce message programmé')}>
            {t('Annuler')}
          </button>
        </div>
      )}
    </div>
  );
  return (
    <div className="px-3 pt-2 space-y-1.5" style={{ backgroundColor: v('bar') }}>
      {shown.map(line)}
      {past.length > 0 && (
        <button type="button" onClick={() => setHistory((h) => !h)} aria-expanded={history}
          className="px-1 text-[12px] underline underline-offset-2" style={{ color: C.meta }}>
          {history ? t('Masquer l’historique') : `${t('Messages programmés — 7 derniers jours')} (${past.length})`}
        </button>
      )}
      {history && past.map(line)}
      {err && <p className="px-1 text-[12px]" style={{ color: C.error }} role="alert">{err.message}</p>}
    </div>
  );
}
